import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Title,
  Button,
  Group,
  Stack,
  Paper,
  Text,
  Table,
  Loader,
  Badge,
  NumberInput,
  Divider,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCalendar, IconDownload, IconRefresh } from '@tabler/icons-react';
import dayjs from 'dayjs';
import SolverDecisions from '../components/SolverDecisions';
import {
  generateSchedule,
  getAssignedShiftsSummary,
  getWeekExportStatus,
  exportWeek,
  getDefaultDateRange,
} from '../api/prediction-api';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function AssignedShiftsPage() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [weeks, setWeeks] = useState<string>('4');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [exportStatuses, setExportStatuses] = useState<Map<string, any>>(new Map());
  const [datesLoaded, setDatesLoaded] = useState(false);
  const initialLoadComplete = useRef(false);
  const prevStartDate = useRef<Date | null>(null);
  const prevWeeks = useRef<string>('');

  // Fetch default dates on mount
  useEffect(() => {
    const loadDefaults = async () => {
      try {
        console.log('Loading default date range...');
        const defaults = await getDefaultDateRange();
        console.log('Received defaults:', defaults);
        const date = dayjs(defaults.start_date).toDate();
        console.log('Parsed date:', date);
        setStartDate(date);
        setDatesLoaded(true);
      } catch (error) {
        console.error('Failed to load default dates, using fallback:', error);
        // Fallback to current week Sunday
        const fallbackDate = dayjs().startOf('week').toDate();
        console.log('Using fallback date:', fallbackDate);
        setStartDate(fallbackDate);
        setDatesLoaded(true);
      }
    };
    loadDefaults();
  }, []);

  // Load data on mount - just fetch summary (only after dates are loaded)
  useEffect(() => {
    if (datesLoaded && startDate && !initialLoadComplete.current) {
      console.log('Initial load - fetching summary');
      fetchSummary();
      initialLoadComplete.current = true;
      prevStartDate.current = startDate;
      prevWeeks.current = weeks;
    }
  }, [datesLoaded, startDate]);

  // Auto-load when date or weeks actually change (not on initial load)
  useEffect(() => {
    if (initialLoadComplete.current && datesLoaded && startDate) {
      const startDateChanged = prevStartDate.current?.getTime() !== startDate.getTime();
      const weeksChanged = prevWeeks.current !== weeks;
      
      if (startDateChanged || weeksChanged) {
        console.log('Date or weeks changed - fetching summary', { startDateChanged, weeksChanged });
        fetchSummary();
        prevStartDate.current = startDate;
        prevWeeks.current = weeks;
      }
    }
  }, [startDate, weeks, datesLoaded]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateSchedule({
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        weeks: parseInt(weeks),
      });
      
      notifications.show({
        title: 'Success',
        message: 'Schedule generated successfully',
        color: 'green',
      });
      
      await fetchSummary();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to generate schedule',
        color: 'red',
      });
    } finally {
      setGenerating(false);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const endDate = dayjs(startDate).add(parseInt(weeks), 'week').subtract(1, 'day');
      
      const data = await getAssignedShiftsSummary(
        dayjs(startDate).format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      
      setSummary(data);
      
      // Fetch export statuses for all weeks
      await fetchExportStatuses(data);
    } catch (error: any) {
      if (!error.message.includes('No schedule run found')) {
        notifications.show({
          title: 'Error',
          message: error.message || 'Failed to load summary',
          color: 'red',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExportStatuses = async (data: any) => {
    if (!data?.weekly_data) return;
    
    const weekStarts: string[] = Array.from(new Set(data.weekly_data.map((w: any) => w.week_start as string)));
    const statuses = new Map();
    
    for (const weekStart of weekStarts) {
      try {
        const status = await getWeekExportStatus(weekStart);
        statuses.set(weekStart, status);
      } catch (error) {
        console.error(`Failed to get export status for ${weekStart}:`, error);
      }
    }
    
    setExportStatuses(statuses);
  };

  const handleExportWeek = async (weekStart: string) => {
    try {
      const result = await exportWeek(weekStart);
      
      if (result.success) {
        notifications.show({
          title: 'Success',
          message: `✓ Exported ${result.shifts_exported} shifts to Square (as drafts). Open Square to publish.`,
          color: 'green',
        });
        
        // Refresh export status
        const status = await getWeekExportStatus(weekStart);
        setExportStatuses(new Map(exportStatuses.set(weekStart, status)));
      } else if (result.assignments_found === 0) {
        notifications.show({
          title: 'No Assignments',
          message: 'No shifts scheduled for this week. Generate assignments first.',
          color: 'blue',
        });
      } else if (result.errors?.some((e: string) => e.includes('published shifts exist'))) {
        notifications.show({
          title: 'Already Published',
          message: 'Shifts already published in Square. Unpublish them in Square Dashboard before re-exporting.',
          color: 'yellow',
        });
      } else {
        notifications.show({
          title: 'Export Failed',
          message: result.errors?.join(', ') || 'Failed to export',
          color: 'red',
        });
      }
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to export week',
        color: 'red',
      });
    }
  };

  if (!summary || !summary.success || !summary.weekly_hours || !summary.employee_totals) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          {/* Header with date controls */}
          <Group justify="space-between">
            <Title order={1}>Assigned Shifts</Title>
            <Group>
              <DatePickerInput
                label="Start Date"
                value={startDate}
                onChange={(date) => date && setStartDate(date)}
                leftSection={<IconCalendar size={16} />}
                placeholder={datesLoaded ? "Select date" : "Loading..."}
                disabled={!datesLoaded}
              />
              <NumberInput
                label="Weeks"
                value={parseInt(weeks)}
                onChange={(value) => setWeeks(String(value || 4))}
                min={1}
                max={52}
                step={1}
                style={{ width: 120 }}
                disabled={!datesLoaded}
              />
              <Button
                onClick={handleGenerate}
                loading={generating}
                leftSection={<IconRefresh size={16} />}
                mt="auto"
              >
                Generate
              </Button>
            </Group>
          </Group>

          {/* Empty state */}
          <Paper p="xl" withBorder>
            <Stack align="center" gap="lg">
              {generating ? (
                <>
                  <Loader size="lg" />
                  <Text>Generating schedule...</Text>
                </>
              ) : (
                <>
                  <Title order={2}>No Schedule Data</Title>
                  <Text c="dimmed">
                    {summary?.error || 'Generate a schedule to see the summary'}
                  </Text>
                </>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    );
  }

  // NOW SAFE TO TRANSFORM - summary exists and has all required fields
  
  // Transform employee totals to match expected field names
  const employeeTotals = (Array.isArray(summary.employee_totals) ? summary.employee_totals : []).map((emp: any) => ({
    employee_id: emp.id,
    employee_name: emp.name,
    total_shifts: emp.shifts,
    total_hours: emp.hours,
    total_target: emp.target || 0,
    avg_per_shift: emp.avg_per_shift,
  }));

  // Transform backend's nested weekly_hours into flat structure
  const weeklyDataFlat: any[] = [];
  if (Array.isArray(summary.weekly_hours)) {
    summary.weekly_hours.forEach((week: any) => {
      if (week && Array.isArray(week.employees)) {
        week.employees.forEach((emp: any) => {
          weeklyDataFlat.push({
            week_start: week.week_start,
            employee_id: emp.employee_id,
            employee_name: emp.employee_name,
            actual_hours: emp.hours,
            actual_shifts: emp.shifts,
            base_target: emp.base_target || 0,
            carry_over: emp.carry_over || 0,
            final_target: emp.final_target || 0,
          });
        });
      }
    });
  }

  // Group weekly data by week
  const weeklyDataByWeek = weeklyDataFlat.reduce((acc: any, item: any) => {
    if (!acc[item.week_start]) {
      acc[item.week_start] = [];
    }
    acc[item.week_start].push(item);
    return acc;
  }, {});

  // Transform backend's weekly_calendar into flat assignments array
  const assignments: any[] = [];
  (summary.weekly_calendar || []).forEach((day: any) => {
    if (day && Array.isArray(day.shifts)) {
      day.shifts.forEach((shift: any) => {
        assignments.push({
          date: day.date,
          day_of_week: day.day_of_week,
          employee_id: shift.employee_id,
          employee_name: shift.employee_name,
          start_time: shift.start_time,
          end_time: shift.end_time,
          duration_hours: shift.hours,
          shift_type: shift.shift_type,
          reason: shift.reason,
        });
      });
    }
  });

  // Group assignments by week and employee
  const assignmentsByWeek = assignments.reduce((acc: any, a: any) => {
    // Calculate week start as Sunday (backend uses Sunday as week start)
    // dayjs().startOf('week') defaults to Monday in some locales
    const date = dayjs(a.date);
    const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, etc.
    const weekStart = date.subtract(dayOfWeek, 'day').format('YYYY-MM-DD');
    
    if (!acc[weekStart]) {
      acc[weekStart] = {};
    }
    if (!acc[weekStart][a.employee_name]) {
      acc[weekStart][a.employee_name] = [];
    }
    acc[weekStart][a.employee_name].push(a);
    return acc;
  }, {});

  const weekStarts = Object.keys(weeklyDataByWeek).sort();

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>Assigned Shifts</Title>
          <Group>
            <DatePickerInput
              label="Start Date"
              value={startDate}
              onChange={(date) => date && setStartDate(date)}
              leftSection={<IconCalendar size={16} />}
              placeholder={datesLoaded ? "Select date" : "Loading..."}
              disabled={!datesLoaded}
            />
            <NumberInput
              label="Weeks"
              value={parseInt(weeks)}
              onChange={(value) => setWeeks(String(value || 4))}
              min={1}
              max={52}
              step={1}
              style={{ width: 120 }}
              disabled={!datesLoaded}
            />
            <Button
              onClick={handleGenerate}
              loading={generating}
              leftSection={<IconRefresh size={16} />}
              mt="auto"
            >
              Generate
            </Button>
          </Group>
        </Group>

        {loading ? (
          <Loader />
        ) : (
          <>
            {/* PERIOD SUMMARY */}
            <Paper p="md" withBorder>
              <Title order={3} mb="md">Period Summary</Title>
              <Text>
                <strong>Period:</strong> {summary.period.start} to {summary.period.end}
              </Text>
              <Text>
                <strong>Total shifts:</strong> {summary.period.total_shifts}
              </Text>
              <Text>
                <strong>Total hours:</strong> {summary.period.total_hours.toFixed(2)}
              </Text>
              <Text>
                <strong>Weeks:</strong> {summary.period.weeks}
              </Text>
              
              {/* SCHEDULE RUNS INFO */}
              {summary.schedule_runs && summary.schedule_runs.length > 0 && (
                <>
                  <Divider my="md" />
                  <Title order={4} mb="md">Schedule Run Details</Title>
                  
                  <Stack gap="xl">
                    {summary.schedule_runs.map((run: any, idx: number) => (
                      <div key={run.runId}>
                        {summary.schedule_runs.length > 1 && (
                          <Title order={5} mb="sm">Run {idx + 1} of {summary.schedule_runs.length}</Title>
                        )}
                        
                        <Group grow>
                          <div>
                            <Text size="sm" c="dimmed">Run ID</Text>
                            <Text size="xs" ff="monospace">{run.runId}</Text>
                          </div>
                          <div>
                            <Text size="sm" c="dimmed">Created</Text>
                            <Text size="sm">{run.createdAt}</Text>
                          </div>
                          <div>
                            <Text size="sm" c="dimmed">Direction</Text>
                            <Badge>{run.direction}</Badge>
                          </div>
                        </Group>
                        
                        <Group grow mt="xs">
                          <div>
                            <Text size="sm" c="dimmed">Schedule Period</Text>
                            <Text size="sm">{run.scheduleStart} to {run.scheduleEnd}</Text>
                          </div>
                          <div>
                            <Text size="sm" c="dimmed">Balance Period</Text>
                            <Text size="sm">{run.balanceStart} to {run.balanceEnd}</Text>
                          </div>
                          <div></div>
                        </Group>
                        
                        {run.totals && (
                          <>
                            <Title order={6} mt="md" mb="sm">Statistics</Title>
                            <Group grow>
                              <div>
                                <Text size="sm" c="dimmed">Total Shifts</Text>
                                <Text fw={500}>{run.totals.totalShifts}</Text>
                              </div>
                              <div>
                                <Text size="sm" c="dimmed">Total Hours</Text>
                                <Text fw={500}>{run.totals.totalHours}h</Text>
                              </div>
                              <div>
                                <Text size="sm" c="dimmed">Fixed Shifts</Text>
                                <Text fw={500}>{run.totals.fixedShifts} ({run.totals.fixedHours}h)</Text>
                              </div>
                              <div>
                                <Text size="sm" c="dimmed">Max Deviation</Text>
                                <Text fw={500} c={run.totals.maxDeviation > 3 ? 'orange' : 'green'}>
                                  {run.totals.maxDeviation.toFixed(2)}h
                                </Text>
                              </div>
                            </Group>
                            
                            <Group grow mt="sm">
                              <div>
                                <Text size="sm" c="dimmed">Total Deviation</Text>
                                <Text fw={500}>{run.totals.totalDeviation.toFixed(2)}h</Text>
                              </div>
                              <div>
                                <Text size="sm" c="dimmed">Min Weekly Hours</Text>
                                <Text fw={500}>{run.totals.minWeeklyHours}h</Text>
                              </div>
                              <div></div>
                              <div></div>
                            </Group>
                          </>
                        )}
                        
                        {idx < summary.schedule_runs.length - 1 && <Divider my="md" />}
                      </div>
                    ))}
                  </Stack>
                </>
              )}
            </Paper>

            {/* HOURS BY EMPLOYEE */}
            <Paper p="md" withBorder>
              <Title order={3} mb="md">Hours by Employee</Title>
              <Text size="sm" c="dimmed" mb="md">(excluding fixed weekday mornings)</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Employee</Table.Th>
                    <Table.Th>Shifts</Table.Th>
                    <Table.Th>Target</Table.Th>
                    <Table.Th>Hours</Table.Th>
                    <Table.Th>Diff</Table.Th>
                    <Table.Th>Avg/Shift</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {employeeTotals.map((emp: any) => {
                    const diff = emp.total_hours - emp.total_target;
                    return (
                      <Table.Tr key={emp.employee_id}>
                        <Table.Td>{emp.employee_name}</Table.Td>
                        <Table.Td>{emp.total_shifts}</Table.Td>
                        <Table.Td><strong>{emp.total_target.toFixed(2)}</strong></Table.Td>
                        <Table.Td>{emp.total_hours.toFixed(2)}</Table.Td>
                        <Table.Td>
                          <Text c={diff >= 0 ? 'green' : 'red'}>
                            {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                          </Text>
                        </Table.Td>
                        <Table.Td>{emp.avg_per_shift.toFixed(2)}h</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>

            {/* WEEKLY CALENDAR VIEW (per week) */}
            <Stack gap="lg">
              {weekStarts.map((weekStart) => {
                const weekEnd = dayjs(weekStart).add(6, 'day').format('YYYY-MM-DD');
                const weekData = weeklyDataByWeek[weekStart];
                const exportStatus = exportStatuses.get(weekStart);
                
                return (
                  <Paper key={weekStart} p="md" withBorder>
                    <Stack gap="md">
                      {/* Week Header */}
                      <Group justify="space-between">
                        <div>
                          <Title order={4}>Week of {dayjs(weekStart).format('MMM D')} - {dayjs(weekEnd).format('MMM D, YYYY')}</Title>
                          {exportStatus?.exported && exportStatus?.exported_at && (
                            <Text size="sm" c="dimmed" mt={4}>
                              Exported {dayjs(exportStatus.exported_at).format('MMM D, YYYY h:mm A')}
                            </Text>
                          )}
                        </div>
                        <Button
                          leftSection={<IconDownload size={16} />}
                          onClick={() => handleExportWeek(weekStart)}
                          size="sm"
                          variant={exportStatus?.exported ? 'light' : 'filled'}
                        >
                          {exportStatus?.exported ? 'Re-export to Square' : 'Export to Square'}
                        </Button>
                      </Group>
                        <Paper p="sm" withBorder>
                          <Title order={5} mb="xs">Weekly Calendar View</Title>
                          <Table>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Employee</Table.Th>
                                {DAY_NAMES.map((day, idx) => {
                                  const date = dayjs(weekStart).add(idx, 'day');
                                  return (
                                    <Table.Th key={day}>
                                      {day} {date.format('DD')}
                                    </Table.Th>
                                  );
                                })}
                                <Table.Th>Shifts</Table.Th>
                                <Table.Th>Hours</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {employeeTotals.map((emp: any) => {
                                const empWeekData = weekData?.find((w: any) => w.employee_id === emp.employee_id);
                                const empAssignments = assignmentsByWeek[weekStart]?.[emp.employee_name] || [];
                                
                                return (
                                  <Table.Tr key={emp.employee_id}>
                                    <Table.Td>{emp.employee_name}</Table.Td>
                                    {DAY_NAMES.map((_, dayIdx) => {
                                      const dayDate = dayjs(weekStart).add(dayIdx, 'day').format('YYYY-MM-DD');
                                      const dayAssignments = empAssignments.filter((a: any) => a.date === dayDate);
                                      
                                      return (
                                        <Table.Td key={`${emp.employee_id}-cal-${dayIdx}`}>
                                          {dayAssignments.length > 0 ? (
                                            <Stack gap={2}>
                                              {dayAssignments.map((a: any, idx: number) => {
                                                // Assign colors based on shift type
                                                const shiftColor = a.shift_type === 'morning_fixed' ? 'blue' 
                                                  : a.shift_type === 'monday_rotation' ? 'grape'
                                                  : 'green';
                                                
                                                return (
                                                  <Badge key={`${dayDate}-${a.start_time}-${idx}`} size="xs" color={shiftColor} variant="light">
                                                    {a.start_time}-{a.end_time} ({a.duration_hours.toFixed(1)}h)
                                                  </Badge>
                                                );
                                              })}
                                            </Stack>
                                          ) : (
                                            '-'
                                          )}
                                        </Table.Td>
                                      );
                                    })}
                                    <Table.Td>{empWeekData?.actual_shifts || 0}</Table.Td>
                                    <Table.Td>{empWeekData?.actual_hours.toFixed(1) || 0}</Table.Td>
                                  </Table.Tr>
                                );
                              })}
                            </Table.Tbody>
                          </Table>
                        </Paper>
                        
                        {/* HOURS BY EMPLOYEE (for this week) */}
                        <div>
                          <Title order={5} mb="xs">Hours by Employee</Title>
                          <Table>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Employee</Table.Th>
                                <Table.Th>Shifts</Table.Th>
                                <Table.Th>Base Target</Table.Th>
                                <Table.Th>Carry Over</Table.Th>
                                <Table.Th>Final Target</Table.Th>
                                <Table.Th>Hours</Table.Th>
                                <Table.Th>Diff</Table.Th>
                                <Table.Th>Avg/Shift</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {employeeTotals.map((emp: any) => {
                                const empWeekData = weekData?.find((w: any) => w.employee_id === emp.employee_id);
                                const hours = empWeekData?.actual_hours || 0;
                                const shifts = empWeekData?.actual_shifts || 0;
                                const baseTarget = empWeekData?.base_target || 0;
                                const carryOver = empWeekData?.carry_over || 0;
                                const finalTarget = empWeekData?.final_target || 0;
                                const diff = hours - finalTarget;
                                const avgPerShift = shifts > 0 ? hours / shifts : 0;
                                
                                return (
                                  <Table.Tr key={emp.employee_id}>
                                    <Table.Td>{emp.employee_name}</Table.Td>
                                    <Table.Td>{shifts}</Table.Td>
                                    <Table.Td>{baseTarget.toFixed(2)}</Table.Td>
                                    <Table.Td>
                                      <Text c={carryOver > 0 ? 'red' : carryOver < 0 ? 'green' : undefined}>
                                        {carryOver > 0 ? '+' : ''}{carryOver.toFixed(2)}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td><strong>{finalTarget.toFixed(2)}</strong></Table.Td>
                                    <Table.Td>{hours.toFixed(2)}</Table.Td>
                                    <Table.Td>
                                      <Text c={diff >= 0 ? 'green' : 'red'}>
                                        {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td>{avgPerShift.toFixed(2)}h</Table.Td>
                                  </Table.Tr>
                                );
                              })}
                            </Table.Tbody>
                          </Table>
                        </div>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>

            {/* MONDAY ROTATION */}
            <Paper p="md" withBorder>
              <Title order={3} mb="md">Monday Rotation (3-of-9)</Title>
              
              {/* Historical Period (9 weeks back) */}
              {summary.monday_rotation?.historical_period && summary.monday_rotation.historical_period.length > 0 && (
                <>
                  <Title order={5} mb="xs" c="dimmed">Historical (9 weeks before schedule)</Title>
                  <Table mb="lg">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Employee</Table.Th>
                        <Table.Th>Source</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {summary.monday_rotation.historical_period
                        .sort((a: any, b: any) => b.date.localeCompare(a.date))
                        .map((shift: any) => (
                          <Table.Tr key={`hist-${shift.date}-${shift.employee_id}`}>
                            <Table.Td>{dayjs(shift.date).format('MMM D, YYYY')}</Table.Td>
                            <Table.Td>{shift.employee_name}</Table.Td>
                            <Table.Td>
                              <Badge size="xs" color="gray" variant="light">
                                {shift.source === 'square' ? 'Square' : 'Scheduled'}
                              </Badge>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                    </Table.Tbody>
                  </Table>
                </>
              )}
              
              {/* Schedule Period */}
              {summary.monday_rotation?.schedule_period && summary.monday_rotation.schedule_period.length > 0 && (
                <>
                  <Title order={5} mb="xs">Schedule Period</Title>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Employee</Table.Th>
                        <Table.Th>Time</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {summary.monday_rotation.schedule_period
                        .sort((a: any, b: any) => a.date.localeCompare(b.date))
                        .map((shift: any) => (
                          <Table.Tr key={`sched-${shift.date}-${shift.employee_id}`}>
                            <Table.Td>{dayjs(shift.date).format('MMM D, YYYY')}</Table.Td>
                            <Table.Td>{shift.employee_name}</Table.Td>
                            <Table.Td>{shift.start_time} - {shift.end_time}</Table.Td>
                          </Table.Tr>
                        ))}
                    </Table.Tbody>
                  </Table>
                </>
              )}
              
              {(!summary.monday_rotation?.schedule_period || summary.monday_rotation.schedule_period.length === 0) && 
               (!summary.monday_rotation?.historical_period || summary.monday_rotation.historical_period.length === 0) && (
                <Text c="dimmed">No Monday rotation data available</Text>
              )}
            </Paper>

            {/* ROTATION COUNTS */}
            {summary.rotation_counts && Array.isArray(summary.rotation_counts) && summary.rotation_counts.length > 0 && (
              <Paper p="md" withBorder>
                <Title order={3} mb="md">Rotation Tracking</Title>
                <Text size="sm" c="dimmed" mb="md">
                  Shows rotation fairness across the 9-week window for each scheduled week
                </Text>
                
                {summary.rotation_counts.map((weekData: any) => {
                  // SAFETY: Check for NEW structure (week_start -> days)
                  if (!weekData.week_start || !Array.isArray(weekData.days)) {
                    console.warn('rotation_counts item missing week_start or days array:', weekData);
                    return null;
                  }
                  
                  return (
                    <div key={weekData.week_start} style={{ marginBottom: '2rem' }}>
                      <Title order={4} mb="md">Week of {dayjs(weekData.week_start).format('MMM D, YYYY')}</Title>
                      
                      {weekData.days.map((dayData: any) => {
                        // SAFETY: Check for required day properties
                        if (!dayData.day_name || !Array.isArray(dayData.employees)) {
                          console.warn('Day missing required properties:', dayData);
                          return null;
                        }
                        
                        return (
                          <div key={dayData.day_of_week} style={{ marginBottom: '1.5rem' }}>
                            <Group gap="xs" mb="xs">
                              <Badge size="lg" variant="light">{dayData.day_name}</Badge>
                              <Text size="xs" c="dimmed">
                                {dayData.reference_date && `Reference: ${dayjs(dayData.reference_date).format('MMM D, YYYY')}`}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {dayData.window_start && `(Window: ${dayjs(dayData.window_start).format('MMM D')} - ${dayjs(weekData.week_start).add(6, 'day').format('MMM D')})`}
                              </Text>
                            </Group>
                            
                            {/* Historical Timeline (if exists) */}
                            {dayData.historical && Array.isArray(dayData.historical) && dayData.historical.length > 0 && (
                              <Paper p="sm" mb="sm" withBorder bg="gray.0">
                                <Text size="xs" fw={600} mb="xs">Historical (9-week window):</Text>
                                <Group gap="xs">
                                  {dayData.historical.map((hist: any, idx: number) => (
                                    <Badge key={idx} size="xs" variant="light">
                                      {dayjs(hist.date).format('MMM D')}: {hist.employee_name}
                                    </Badge>
                                  ))}
                                </Group>
                              </Paper>
                            )}
                            
                            {/* Assigned This Week */}
                            {dayData.assigned && (
                              <Paper p="xs" mb="sm" withBorder bg="green.0">
                                <Group gap="xs">
                                  <Badge size="sm" color="green" variant="filled">Assigned</Badge>
                                  <Text size="sm" fw={600}>{dayData.assigned.employee_name}</Text>
                                  <Text size="xs" c="dimmed">
                                    ({dayjs(dayData.assigned.date).format('ddd, MMM D')})
                                  </Text>
                                </Group>
                              </Paper>
                            )}
                            
                            {/* Employee Rotation Counts */}
                            <Table>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Employee</Table.Th>
                                  <Table.Th>Count Before Assignment</Table.Th>
                                  <Table.Th>Assigned This Week</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {dayData.employees
                                  .sort((a: any, b: any) => a.historical_count_before_assignment - b.historical_count_before_assignment)
                                  .map((emp: any) => (
                                    <Table.Tr 
                                      key={emp.employee_id}
                                      bg={emp.assigned_this_week ? 'green.0' : undefined}
                                    >
                                      <Table.Td>
                                        <Text fw={emp.assigned_this_week ? 600 : 400}>
                                          {emp.employee_name}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Badge 
                                          size="sm" 
                                          variant="light"
                                          color={emp.historical_count_before_assignment === 0 ? 'blue' : 'gray'}
                                        >
                                          {emp.historical_count_before_assignment}
                                        </Badge>
                                      </Table.Td>
                                      <Table.Td>
                                        <Badge 
                                          color={emp.assigned_this_week ? 'green' : 'gray'} 
                                          variant={emp.assigned_this_week ? 'filled' : 'light'}
                                        >
                                          {emp.assigned_this_week ? '✓ Yes' : '— No'}
                                        </Badge>
                                      </Table.Td>
                                    </Table.Tr>
                                  ))}
                              </Table.Tbody>
                            </Table>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                
                {summary.rotation_counts.length === 0 && (
                  <Text c="dimmed">No rotation tracking data available</Text>
                )}
              </Paper>
            )}

            {/* STEPS SUMMARY */}
            {summary.steps_summary && summary.steps_summary.length > 0 && (
              <Paper p="md" withBorder>
                <Title order={3} mb="md">Scheduling Algorithm Steps</Title>
                <Text c="dimmed" mb="lg">
                  Shows which shifts were assigned at each algorithm step
                </Text>
                
                {summary.steps_summary.map((week: any) => (
                  <div key={week.week_start} style={{ marginBottom: '2rem' }}>
                    <Title order={5} mb="sm">Week of {dayjs(week.week_start).format('MMM D, YYYY')}</Title>
                    
                    {Array.isArray(week.steps) && week.steps.map((step: any) => {
                      const shifts = step.shifts || [];
                      
                      // Safety check: ensure step has required properties
                      if (!step.step_name || !step.step_order) {
                        console.warn('Step missing required properties:', step);
                        return null;
                      }
                      
                      // Group shifts by employee for this step
                      const shiftsByEmployee: Record<string, any[]> = {};
                      shifts.forEach((shift: any) => {
                        if (!shiftsByEmployee[shift.employee_name]) {
                          shiftsByEmployee[shift.employee_name] = [];
                        }
                        shiftsByEmployee[shift.employee_name].push(shift);
                      });
                      
                      const employees = Object.keys(shiftsByEmployee).sort();
                      
                      return (
                        <Paper key={step.step_order} p="sm" withBorder mb="md" bg="gray.0">
                          <Group mb="sm">
                            <Badge size="lg" variant="filled">{step.step_order}</Badge>
                            <Text fw={500} size="lg">{step.step_name}</Text>
                            <Badge 
                              size="sm" 
                              color={step.run_type === 'morning' ? 'blue' : 'grape'}
                              variant="light"
                            >
                              {step.run_type}
                            </Badge>
                          </Group>
                          
                          {shifts.length > 0 ? (
                            <Table>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Employee</Table.Th>
                                  {DAY_NAMES.map((day, idx) => {
                                    const date = dayjs(week.week_start).add(idx, 'day');
                                    return (
                                      <Table.Th key={day}>
                                        {day} {date.format('DD')}
                                      </Table.Th>
                                    );
                                  })}
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {employees.map((empName: string) => {
                                  const empShifts = shiftsByEmployee[empName];
                                  
                                  return (
                                    <Table.Tr key={empName}>
                                      <Table.Td>{empName}</Table.Td>
                                      {DAY_NAMES.map((_, dayIdx) => {
                                        const dayDate = dayjs(week.week_start).add(dayIdx, 'day').format('YYYY-MM-DD');
                                        const dayShifts = empShifts.filter((s: any) => s.shift_date === dayDate);
                                        
                                        return (
                                          <Table.Td key={`${empName}-${dayIdx}`}>
                                            {dayShifts.length > 0 ? (
                                              <Stack gap={2}>
                                                {dayShifts.map((shift: any, idx: number) => (
                                                  <div key={idx}>
                                                    <Badge size="xs" color="blue" variant="light">
                                                      {shift.start_time}-{shift.end_time} ({shift.hours.toFixed(1)}h)
                                                    </Badge>
                                                    <Text size="xs" c="dimmed">{shift.reason}</Text>
                                                  </div>
                                                ))}
                                              </Stack>
                                            ) : (
                                              '-'
                                            )}
                                          </Table.Td>
                                        );
                                      })}
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          ) : (
                            <Text c="dimmed" fs="italic" size="sm">No shifts assigned in this step</Text>
                          )}
                          
                          {/* Show solver decisions for step 3 (optimal_assignment) */}
                          {step.step_name === 'optimal_assignment' && 
                           shifts.length > 0 && 
                           week.week_start && (
                            <div style={{ marginTop: '1rem' }}>
                              <SolverDecisions weekStart={week.week_start} />
                            </div>
                          )}
                        </Paper>
                      );
                    })}
                  </div>
                ))}
              </Paper>
            )}

          </>
        )}
      </Stack>
    </Container>
  );
}
